// Eskom PARCC — canonical division folding (Lindiwe R4 feedback #3).
//
// Live transaction data carries several spellings of the same division —
// "GX- Head Office" vs "Gx", "Tactical Procurement" vs "Tactical Sourcing",
// "ICT and Professional Servises" [sic] vs "ICT & Professional Services". A
// reviewer / team lead is scoped to their grant's division, so a spelling
// mismatch makes them see NONE of their division. Rather than a one-off data
// relabel (which the next import would undo), fold every known variant onto ONE
// canonical label at the boundary — used by BOTH the scoping comparison
// (permissions / registerScope) and the dashboard / report / filter grouping — so
// variants match and display under the right division, and future variants are
// caught automatically. No data migration.
import { DIVISION_OPTIONS } from './enums';

const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Fold a raw division label onto its canonical {@link DIVISION_OPTIONS} name.
 * Unknown labels pass through trimmed (a genuinely new division still
 * self-matches). "ICT" collapses into "ICT & Professional Services" — the live
 * data and the team treat them as one division.
 */
export function canonicalDivision(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const n = norm(trimmed);
  // Alias rules (substring, so typos/variants fold): order matters.
  if (n.includes('ntcsa')) return 'NTCSA';
  if (n.includes('ict') || n.includes('professional serv')) return 'ICT & Professional Services';
  if (n.includes('tactical')) return 'Tactical Sourcing';
  if (n.includes('gcd')) return 'GCD';
  if (n.startsWith('gx') || n.includes('head office')) return 'Gx';
  if (n.includes('ped')) return 'PED';
  if (n.includes('ccd')) return 'CCD';
  // Exact (case-insensitive) match to any remaining canonical option.
  const exact = DIVISION_OPTIONS.find((o) => norm(o) === n);
  return exact ?? trimmed;
}

/** The lowercased comparison key for a division (canonical fold, then lowercase). */
export function divisionKey(raw: string | null | undefined): string {
  return canonicalDivision(raw).toLowerCase();
}
